'use client';

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import type { UserData } from 'services/generated/auth-service/models/UserData';
import { useStore } from 'zustand';
import { createCartStore, type CartStore } from './cartStore';
import { createUserStore, type UserStore } from '@/stores/userStore';
import { getCart } from '@/actions/cartActions';
import useLocale from '@/hooks/useLocale';
import { getMembershipInfo } from '@/client/usersClient';
import { CART_LOCAL_STORAGE_NAME } from '@/lib/constants';
import { DeviceGeneration } from 'ui/utils/constants/maverick';

// User Store Context
const UserStoreContext = createContext<ReturnType<
  typeof createUserStore
> | null>(null);

// Cart Store Context
const CartStoreContext = createContext<ReturnType<
  typeof createCartStore
> | null>(null);

interface StoreProviderProps {
  children: ReactNode;
  initialUser?: UserData | undefined;
  initialAuthToken?: string | undefined;
  initialStrap?: DeviceGeneration | null;
}

export function StoreProvider({
  children,
  initialUser,
  initialAuthToken,
  initialStrap,
}: StoreProviderProps) {
  // Create store instances with server data using useRef to prevent recreation
  const userStoreRef = useRef<ReturnType<typeof createUserStore>>();
  const cartStoreRef = useRef<ReturnType<typeof createCartStore>>();

  if (!userStoreRef.current) {
    userStoreRef.current = createUserStore({
      user: initialUser,
      authToken: initialAuthToken,
      isLoggedIn: Boolean(initialUser),
      strap: initialStrap, // Pass the strap here
    });
  }

  if (!cartStoreRef.current) {
    cartStoreRef.current = createCartStore({
      // Cart will be initialized from cookies on client side
      cart: null,
      userCheckoutDetails: null,
      isCartOpen: false,
    });
  }

  // Cart initialization logic
  const { language, country } = useLocale();

  const authToken = useStore(userStoreRef?.current, (state) => state.authToken);
  const { cartId, setCart, setIsLoadingCart } = useStore(
    cartStoreRef?.current,
    (state) => ({
      cartId: state.cart?.id,
      setCart: state.setCart,
      setIsLoadingCart: state.setIsLoadingCart,
    }),
  );

  // Initialize cart from server data if available
  useEffect(() => {
    const loadCart = async () => {
      setIsLoadingCart(true);
      const derivedAuthToken = authToken || initialAuthToken;
      const derivedCartId =
        cartId || localStorage?.getItem(CART_LOCAL_STORAGE_NAME) || undefined;

      if (!derivedAuthToken && !derivedCartId) {
        setIsLoadingCart(false);
        return;
      }

      try {
        const loadedCart = await getCart({
          authToken: derivedAuthToken,
          cartId: derivedCartId,
          language,
          country,
        });
        setCart(loadedCart);
      } catch (error) {
        console.error('Failed to load cart:', error);
        // TODO: Log in sentry
      } finally {
        setIsLoadingCart(false);
      }
    };

    loadCart();
  }, [language, country, initialAuthToken, cartId, authToken]);

  // Load membership info on client side
  // We could lift this layout.tsx file if we need to render something based on membership info
  // without a page flicker
  useEffect(() => {
    if (!authToken) return;
    // load and populate user store with membership info
    const loadMembership = async () => {
      const membership = await getMembershipInfo(authToken);
      if (membership?.email) {
        userStoreRef.current?.getState().setEmail(membership.email);
        userStoreRef.current?.getState().setMembership(membership);
      }
    };
    loadMembership();
  }, [authToken]);

  return (
    <UserStoreContext.Provider value={userStoreRef.current}>
      <CartStoreContext.Provider value={cartStoreRef.current}>
        {children}
      </CartStoreContext.Provider>
    </UserStoreContext.Provider>
  );
}

// User Store Hook
export function useUserStore<T>(selector: (state: UserStore) => T) {
  const store = useContext(UserStoreContext);
  if (!store) {
    throw new Error('useUserStore must be used within StoreProvider');
  }
  return useStore(store, selector);
}

// Cart Store Hook
export function useCartStore<T>(selector: (state: CartStore) => T) {
  const store = useContext(CartStoreContext);
  if (!store) {
    throw new Error('useCartStore must be used within StoreProvider');
  }
  return useStore(store, selector);
}
